import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { activateProvToken } from '../provApi';

const BRAND_COLOR = '#1a365d';

interface ProvSetupProps {
  onSetupComplete: () => void;
  reason?: 'stale' | 'disconnected' | null;
}

export default function ProvSetup({ onSetupComplete, reason = null }: ProvSetupProps) {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedToken = token.trim();
    if (trimmedToken.length < 10) {
      setError('Token måste vara minst 10 tecken.');
      return;
    }

    setLoading(true);
    setError(null);

    const isValid = await activateProvToken(trimmedToken);

    if (isValid) {
      onSetupComplete();
    } else {
      setError('Ogiltig token. Kontrollera att du klistrat in rätt värde.');
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BRAND_COLOR,
        pt: 'env(safe-area-inset-top)',
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <Box sx={{ py: 2, px: { xs: 2, sm: 4 } }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
          Provstationen
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <KeyIcon sx={{ fontSize: 48, color: BRAND_COLOR, mb: 1.5 }} />
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                {reason === 'stale' ? 'Ny token behövs' : 'Prov-konfiguration'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {reason === 'stale'
                  ? 'Den sparade tokenen gäller inte längre. Klistra in en ny prov-token.'
                  : reason === 'disconnected'
                    ? 'Enheten är frånkopplad. Ange token för att aktivera igen.'
                    : 'Ange prov-token för att aktivera läxförhör'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Prov-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                type={showToken ? 'text' : 'password'}
                autoComplete="off"
                placeholder="Klistra in token..."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showToken ? 'Dölj token' : 'Visa token'}
                        onClick={() => setShowToken((value) => !value)}
                        edge="end"
                      >
                        {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': { minHeight: 52, borderRadius: 2 },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || token.trim().length === 0}
                sx={{
                  minHeight: 52,
                  fontSize: '1rem',
                  borderRadius: 2,
                  bgcolor: BRAND_COLOR,
                  '&:hover': { bgcolor: '#0f2744' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Aktivera provstationen'}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
              Kontakta förälder/admin om du inte har en token
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
